import type { SalesPersonResponse } from "@finn-no/mobility-dealer-types";
import type {
  GalleryImage,
  LatestAdsBySearchKey,
  OrganisationResponse,
  OtherLocation,
} from "@mobility-item-page/dealer";
import React from "react";

const DealerDataContext = React.createContext<
  | {
      organisation: OrganisationResponse;
      otherLocations: OtherLocation[];
      latestAds: LatestAdsBySearchKey[];
      images: GalleryImage[];
      latestGroupAds: LatestAdsBySearchKey[];
      salesPersons: SalesPersonResponse[];
    }
  | undefined
>(undefined);

type DealerDataProviderProps = {
  children: React.ReactNode;
  organisation: OrganisationResponse;
  otherLocations: OtherLocation[];
  latestAds: LatestAdsBySearchKey[];
  images: GalleryImage[];
  latestGroupAds: LatestAdsBySearchKey[];
  salesPersons: SalesPersonResponse[];
};

export function OrganisationProvider(props: DealerDataProviderProps) {
  const { children, organisation, otherLocations, latestAds, images, latestGroupAds, salesPersons } = props;

  return (
    <DealerDataContext value={{ organisation, otherLocations, latestAds, images, latestGroupAds, salesPersons }}>
      {children}
    </DealerDataContext>
  );
}

export function useOrganisation() {
  const context = React.useContext(DealerDataContext);
  if (typeof context === "undefined") {
    throw new Error("useOrganisation must be used within an DealerDataProvider");
  }
  return context.organisation;
}

export function useOtherLocations() {
  const context = React.useContext(DealerDataContext);
  if (typeof context === "undefined") {
    throw new Error("useOtherLocations must be used within an DealerDataProvider");
  }
  return { otherLocations: context.otherLocations, latestGroupAds: context.latestGroupAds };
}

export function useLatestAds() {
  const context = React.useContext(DealerDataContext);
  if (typeof context === "undefined") {
    throw new Error("useLatestAds must be used within an DealerDataProvider");
  }
  return context.latestAds;
}

export function useImages() {
  const context = React.useContext(DealerDataContext);
  if (typeof context === "undefined") {
    throw new Error("useImages must be used within an DealerDataProvider");
  }
  return context.images;
}

export function useSalesPersons() {
  const context = React.useContext(DealerDataContext);
  if (typeof context === "undefined") {
    throw new Error("useSalesPersons must be used within an DealerDataProvider");
  }
  return context.salesPersons;
}
